import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAncillaries } from '@aviata/booking-composable';
import { createAncillaryData } from '@aviata/booking-data';
import { createStorage } from '@aviata/storage';
import type { PassengerKey } from '@aviata/booking-data';

import { bookingStateModel } from '@/features/booking-state';

import { mappedPassengersModel } from '@/entities/passenger-with-baggages';

import { api } from '@/shared/api';
import { sentryModel } from '@/shared/model';

const ANCILLARIES_STORAGE_KEY = 'free-avia:ancillaries';

export const useAncillariesStore = defineStore('ancillariesStore', () => {
  const bookingStore = bookingStateModel.useBookingStore();
  const passengersWithBaggage = mappedPassengersModel.useBaggageData();
  const ancillariesStorage = createStorage(sessionStorage);

  const isBaggageAvailable = computed(
    () => Number(passengersWithBaggage.data.value?.passengers.length) > 0,
  );

  /**
   * Since ancillaries is initialized only once
   * to prevent sending redundant queries, we need to clear the store.
   * This store is cleared on SearchPage,
   * because ancillaries query will return new data only when offer id is changed.
   * */
  const ancillaries = ref<ReturnType<typeof useAncillaries> | undefined>();
  const isAncillariesLoading = ref(false);

  const initAncillaries = (offerId: string) => {
    /** Need to init ancillaries only it wasn't initialized before */
    if (!ancillaries.value) {
      const ancillariesData = createAncillaryData(
        ancillariesStorage,
        ANCILLARIES_STORAGE_KEY,
      );
      const passengerKeys = Object.keys(
        bookingStore.state.passengers,
      ) as PassengerKey[];
      const ancillariesState = ancillariesData.init(passengerKeys);

      ancillaries.value = useAncillaries({
        offerId,
        fetchAncillaries(offerId: string) {
          return api.airflow.ancillaries(offerId).then(res => res.data);
        },
      });

      if (ancillaries.value) {
        isAncillariesLoading.value = true;
        ancillaries.value
          .init({ keys: passengerKeys, state: ancillariesState })
          .then(() => {
            passengersWithBaggage.init(
              bookingStore.state.passengers,
              ancillaries.value.structured,
              bookingStore.mappedFlights,
            );
          })
          .catch(err => {
            sentryModel.captureException(
              sentryModel.getReadableError(
                'ERR_FAILED_TO_INIT_ANCILLARIES',
                err,
              ),
            );
          })
          .finally(() => (isAncillariesLoading.value = false));
      }
    } else {
      passengersWithBaggage.init(
        bookingStore.state.passengers,
        ancillaries.value.structured,
        bookingStore.mappedFlights,
        ancillaries.value?.state,
      );
    }
  };

  /**
   * Wipes every place an ancillary product id can linger: the Pinia
   * singleton, the persisted seed in sessionStorage that `initAncillaries`
   * re-hydrates from, and the bookingStore copy that `mapAncillaries`
   * folds into the order payload. Callers fire this when the ancillary
   * map is about to be invalidated (passenger swap, fare change, fresh
   * search) — without dropping all three, stale ids leak into
   * POST /airflow/orders as "Product not found" on `products.N`.
   */
  const clearState = () => {
    ancillaries.value = undefined;
    passengersWithBaggage.data.value = undefined;
    ancillariesStorage.remove(ANCILLARIES_STORAGE_KEY);
    bookingStore.update({ ancillaries: {} });
  };

  return {
    ancillaries,
    passengersWithBaggage,
    initAncillaries,
    isAncillariesLoading,
    isBaggageAvailable,
    clearState,
  };
});
